import {
  Header,
  HeaderName,
  HeaderGlobalBar,
  HeaderGlobalAction,
  HeaderPanel,
  SkipToContent,
  HeaderNavigation,
  Switcher,
  SwitcherItem,
  Modal,
  Tag,
  Link,
} from "carbon-components-react";
import _ from "lodash"
import classNames from "classnames"
import { Search20, ServerDns20, Notification20, WifiOff20 } from "@carbon/icons-react";
import { useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { getCountries, getLanguages, getTimezones, logout, readNotifications, getNotifications } from "api/functions/functions";
import { setAuthToken } from "utils/functions";
import useUser from "hooks/useUser";
import SupportButton, { SUPPRT_BROWSER } from "ui/SupportButton/SupportButton";
import useModalTestPlayer from "hooks/useModalTestPlayer";
import { CloudMonitoring20 } from "@carbon/icons-react";
import MainContext from "context/MainContext";
import { CONFIGS, USERS } from "api/config/endpoints";
import Button from "ui/Button";
import "./Navbar.scss"

const supportUri = process.env.REACT_APP_SUPPORT_URL

const Navbar = ({ onChangePasswordClicked, ...props }) => {
  // eslint-disable-next-line
  const [search, setSearch] = useState(false);
  const [panel, setPanel] = useState(null);
  const [panelNotifications, setPanelNotifications] = useState(false);
  const [user, setUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshingLocalStorage, setRefreshingLocalStorage] = useState(false);
  const context = useContext(MainContext)
  const [loadingRead, setLoadingRead] = useState(false)

  const {
    openTestPlayer
  } = useModalTestPlayer()

  useEffect(() => {
    setUser(props.user)
  }, [props.user])

  useEffect(() => {
    if (props.browserAlert) {
      setModalOpen(true)
    }
    else {
      setModalOpen(false)
    }
  }, [props.browserAlert])

  const {
    getPrettyUserName,
    userIsAdmin,
    userIsSuperAdmin,
    userIsMcr
  } = useUser()

  const togglePanel = (event, panelName) => {
    event.target.blur();
    setPanelNotifications(false)
    setPanel(panelName === panel ? null : panelName);
  };

  const togglePanelNotifications = (event, panelName) => {
    event.target.blur();
    setPanel(null)
    setPanelNotifications(!panelNotifications);
  };

  const onLogoutClicked = async () => {
    await logout()
    setAuthToken()
    window.location.replace("/login");
    window.Pusher.unsubscribe("private-user-" + user.id)
  }

  const refreshLocalStorage = async () => {
    setRefreshingLocalStorage(true)
    await getLanguages(true)
    await getTimezones(true)
    await getCountries(true)
    setRefreshingLocalStorage(false)
  }

  const markNotificationAsRead = async (notificationID) => {
    setLoadingRead(true)
    try {
      const resp = await readNotifications(notificationID)
      // Remove from state read element
      context.setUserNotifications(prevValue => prevValue.filter(el => el.id !== resp.id))
    } catch (error) {
      console.log("error", error)
    } finally {
      setLoadingRead(false)
    }
  }

  const style = (props.online) ? {} : {
    backgroundColor: "#e11"
  }

  let environmentLabelLabel = process.env.REACT_APP_ENV
  let environmentLabelColor = null

  switch (process.env.REACT_APP_ENV) {
    case "local":
      environmentLabelColor = "gray"
      break
    case "staging":
      environmentLabelColor = "green"
      break
    default:
      environmentLabelColor = "red"
      environmentLabelLabel = "Production"
      break
  }

  const loggedOnly = (user && !_.isEmpty(user)) ? (
    <>
      <HeaderName href="/" prefix="">
        ADaM {panel} <Tag type={environmentLabelColor}>{environmentLabelLabel}</Tag>
      </HeaderName>
      <HeaderNavigation aria-label="IBM [Platform]">
        {
          userIsMcr() ? null : (
            <>
              <li>
                <NavLink to="/events" className="bx--header__menu-item">
                  <span className="bx--text-truncate--end">
                    Events
                  </span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/calendar" className="bx--header__menu-item">
                  <span className="bx--text-truncate--end">
                    Calendar
                  </span>
                </NavLink>
              </li>
              <li>
                <NavLink to="/speakers" className="bx--header__menu-item">
                  <span className="bx--text-truncate--end">
                    Speakers
                  </span>
                </NavLink>
              </li>
            </>
          )
        }
        {
          userIsSuperAdmin() ? (
            <>
              <li>
                <NavLink to="/domains" className="bx--header__menu-item">
                  <span className="bx--text-truncate--end">
                    Domains
                  </span>
                </NavLink>
              </li>
              <li>
                <NavLink to={`/${CONFIGS}/${USERS}`} className="bx--header__menu-item">
                  <span className="bx--text-truncate--end">
                    Config
                  </span>
                </NavLink>
              </li>
            </>
          ) : null
        }
        {
          userIsAdmin() ? (
            <li>
              <NavLink to="/logs" className="bx--header__menu-item">
                <span className="bx--text-truncate--end">
                  Logs
                </span>
              </NavLink>
            </li>
          ) : null
        }
      </HeaderNavigation>

      <HeaderGlobalBar>
        {search ? (
          <HeaderGlobalAction aria-label="Search" onClick={null}>
            <Search20 />
          </HeaderGlobalAction>
        ) : null}

        {!context.isPusherOnline && 
          <HeaderGlobalAction
            aria-label="pusher status"
          >
            <WifiOff20/>
          </HeaderGlobalAction>
        }
       {context.isStagingAndUserNotAdmin ? null : <HeaderGlobalAction
          aria-label="Handling domains"
          onClick={() => {
            context.setModalCheckBuyDomainValue("")
            context.setModalCheckBuyDomainOpen(true)
          }}
          isActive={panel === "notifications"}
        >
          <ServerDns20 />
        </HeaderGlobalAction>}

        <HeaderGlobalAction
          aria-label="Test player"
          onClick={() => openTestPlayer()}
        >
          <CloudMonitoring20 />
        </HeaderGlobalAction>

        <HeaderGlobalAction
          aria-label="Notifications"
          onClick={(e) => togglePanelNotifications(e)}
          isActive={panelNotifications}
        >
          <Notification20 />
          {context?.userNotifications?.length > 0 && <span className="notification-status"/>}
        </HeaderGlobalAction>
        <HeaderGlobalAction
          aria-label="App Switcher"
          onClick={(e) => togglePanel(e, "profile")}
          isActive={panel === "profile"}
        >
          {getPrettyUserName()}
        </HeaderGlobalAction>
      </HeaderGlobalBar>
      {/* User */}
      <HeaderPanel aria-label="Header Panel" expanded={panel !== null} >
        <Switcher aria-label="items">
          <SwitcherItem aria-label="support (external)" href={supportUri} target="_blank">
            Support (External)
          </SwitcherItem>
          <SwitcherItem aria-label="refresh info" onClick={refreshLocalStorage}>
            {refreshingLocalStorage ? "Refreshing..." : "Refresh"}
          </SwitcherItem>
          <SwitcherItem aria-label="change password" onClick={onChangePasswordClicked}>
            Change Password
          </SwitcherItem>
        </Switcher>
        <Switcher aria-label="logout wrapper">
          <SwitcherItem aria-label="logout" onClick={onLogoutClicked}>
            Logout
          </SwitcherItem>
        </Switcher>
      </HeaderPanel>

      {/* Notifications */}
      <HeaderPanel aria-label="Header Panel Notifications" expanded={panelNotifications} style={panelNotifications ? {width: "25rem", overflowY: "auto"} : undefined} >
          <div className="notification-status__list">
            {context?.userNotifications?.length > 0 ? context?.userNotifications?.map((el,i) => {
              return <div key={i} className="notification-status__list-wrapper">
                        <div className="notification-status__list-item">
                          <h4 style={{fontWeight: "bold"}} className={"notification-status__list-item-title"}>{el?.data?.title}</h4>
                          <span className={"notification-status__list-item-message"}>{el?.data?.message}</span>
                        </div>
                        <div className="notification-status__list-item-button-box">
                          <div>
                            <Button size={'small'} onClick={() => markNotificationAsRead(el?.id)}>
                              Mark as read
                            </Button>
                          </div>
                        </div>
                        {(i + 1 !== context?.userNotifications?.length) && <hr className="divider"/>}
                     </div>
                     
            }) : <span className="notification-status__list-empty">There are no notifications</span>}
          </div>
      </HeaderPanel>
    </>
  ) : null

  return (
    <>
      <Modal
        open={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        modalHeading="Browser error!"
        size="xs"
        passiveModal
      >
        <div>
          Unsafe browser version detected.<br />
          The recommended browser is Google Chorme.<br />
          Please download <a href="https://www.google.it/intl/it/chrome/" target="_blank" rel="noreferrer">here</a>
        </div>
        <div style={{ marginTop: "1rem" }}>
          <SupportButton supportType={SUPPRT_BROWSER} kind={"danger"} />
        </div>
      </Modal>


      <Header
        aria-label="ADaM Event manager"
        className={classNames("navbar",{
          offline: !props.online
        })}
        style={style}
      >
        <SkipToContent />
        {/* <HeaderMenuButton
        aria-label="Open menu"
        isCollapsible
      // onClick={onClickSideNavExpand}
      // isActive={isSideNavExpanded}
      /> */}
        {loggedOnly}
      </Header>
    </>
    // <ModalRefreshLocalStorage
  );
};

export default Navbar;
